import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardHat } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const data = await login(email, password);
      navigate(data.role === "worker" ? "/objects" : from, { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Ошибка входа");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    localStorage.removeItem("app_token");
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] grain-bg flex items-stretch">
      {/* Left: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
              <HardHat className="text-amber-400" size={22} />
            </div>
            <div>
              <div className="font-display font-extrabold text-2xl text-amber-500 leading-none">Grandmont</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Group</div>
            </div>
          </div>

          <h1 className="font-display font-bold text-3xl mb-2 text-slate-100">Добро пожаловать</h1>
          <p className="text-slate-400 text-sm mb-8">Внутренняя система управления. Войдите, чтобы продолжить.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300 mb-1.5 block">Эл. почта</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#131924] border-[#212b3d] focus:border-amber-500 focus:ring-amber-500/20 text-slate-100 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300 mb-1.5 block">Пароль</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-[#131924] border-[#212b3d] focus:border-amber-500 focus:ring-amber-500/20 text-slate-100 h-11"
              />
            </div>

            {err && (
              <div data-testid="login-error" className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md p-2.5">
                {err}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              {busy ? "Вход…" : "Войти"}
            </Button>

            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 h-px bg-[#212b3d]" />
              <span className="text-[10px] uppercase tracking-widest text-slate-500">или</span>
              <div className="flex-1 h-px bg-[#212b3d]" />
            </div>

            <button
              type="button"
              data-testid="google-signin-button"
              onClick={googleSignIn}
              className="w-full h-11 flex items-center justify-center gap-3 rounded-md bg-[#131924] border border-[#212b3d] text-slate-200 hover:bg-[#1c2434] transition font-medium"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Войти через Google
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-xs">
            <Link to="/forgot-password" className="text-slate-400 hover:text-amber-400 transition">
              Забыли пароль?
            </Link>
          </div>

        </div>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-l border-[#212b3d]">
        <img
          src="https://images.unsplash.com/photo-1664662568348-24b1482b6354?w=1400&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b0f17] via-[#0b0f17]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12 max-w-xl">
          <div className="text-amber-400 text-xs uppercase tracking-[0.3em] mb-3 font-medium">Chemnitz · Dresden · Leipzig</div>
          <h2 className="font-display font-extrabold text-5xl leading-tight text-slate-100 mb-4">
            Ваши стройки — <span className="text-amber-500">под контролем.</span>
          </h2>
          <p className="text-slate-300 text-base leading-relaxed">
            Trockenbau, Malerarbeiten, Bodenbelag, Dämmung, Fenster, Fassade — все объекты, клиенты,
            КП и финансы в одном месте.
          </p>
        </div>
      </div>
    </div>
  );
}
